'use client';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useCallback, useId, useMemo, useRef, useState, } from 'react';
import { cx } from '../../../internal/cx.js';
import { CloseGlyph, CopyGlyph } from '../../../internal/icons.js';
import { Button, IconButton } from '../../actions/button/index.js';
import { NativeSelect } from '../../forms/form-field/index.js';
import { Switch } from '../../forms/switch/index.js';
import {} from '../../../internal/dates/index.js';
/**
 * Checks every day: times fall within the day (`range`), each interval ends
 * after it starts (`order`) and no two intervals overlap (`overlap`). Returns
 * the first problem per ISO weekday; an empty object means the value is valid.
 */
export function validateWeeklyHours(value) {
    const out = {};
    for (let day = 1; day <= 7; day++) {
        const ranges = value.filter((r) => r.day === day);
        let error;
        if (ranges.some((r) => r.start < 0 || r.end > 1440))
            error = 'range';
        else if (ranges.some((r) => r.end <= r.start))
            error = 'order';
        else {
            const sorted = [...ranges].sort((a, b) => a.start - b.start);
            for (let i = 1; i < sorted.length; i++)
                if ((sorted[i]?.start ?? 0) < (sorted[i - 1]?.end ?? 0))
                    error = 'overlap';
        }
        if (error)
            out[day] = error;
    }
    return out;
}
const DEFAULT_ERRORS = {
    range: 'Times must fall within the day',
    order: 'Each interval must end after it starts',
    overlap: 'Intervals must not overlap',
};
function PlusGlyph() {
    return (_jsx("svg", { width: 16, height: 16, viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: 1.75, strokeLinecap: "round", "aria-hidden": true, focusable: false, children: _jsx("path", { d: "M8 3.5v9M3.5 8h9" }) }));
}
function dayName(day, locale) {
    // 2024-01-01 was a Monday.
    return new Intl.DateTimeFormat(locale, { weekday: 'long', timeZone: 'UTC' }).format(new Date(Date.UTC(2024, 0, day, 12)));
}
function uses12h(locale, hourCycle) {
    if (hourCycle)
        return hourCycle === 'h12';
    const resolved = new Intl.DateTimeFormat(locale, { hour: 'numeric' }).resolvedOptions().hourCycle;
    return resolved === 'h12' || resolved === 'h11';
}
function timeLabel(minutes, locale, hourCycle) {
    if (minutes >= 1440)
        return uses12h(locale, hourCycle) ? 'Midnight' : '24:00';
    return new Intl.DateTimeFormat(locale, {
        hour: uses12h(locale, hourCycle) ? 'numeric' : '2-digit',
        minute: '2-digit',
        hourCycle,
        timeZone: 'UTC',
    }).format(new Date(Date.UTC(2000, 0, 1, Math.floor(minutes / 60), minutes % 60)));
}
const byDay = (value) => {
    const map = new Map();
    for (let d = 1; d <= 7; d++)
        map.set(d, []);
    for (const r of value)
        map.get(r.day)?.push({ start: r.start, end: r.end });
    return map;
};
const flatten = (map) => [1, 2, 3, 4, 5, 6, 7].flatMap((day) => (map.get(day) ?? []).map((r) => ({ day, ...r })));
/** After the last interval: an hour later and an hour long, within the day. */
function nextInterval(ranges, step) {
    const last = [...ranges].sort((a, b) => a.start - b.start).at(-1);
    if (!last)
        return { start: 540, end: 1020 };
    const start = Math.min(last.end + 60, 1440 - step);
    return { start, end: Math.min(start + 60, 1440) };
}
/**
 * Edits weekly availability: each day switched on or off, one or more time
 * intervals per day, copying a day to the whole week and overlap checks. The
 * value is a plain list of `{ day, start, end }` intervals in minutes.
 */
export const WeeklyHoursEditor = forwardRef(function WeeklyHoursEditor({ value: valueProp, defaultValue, onValueChange, legend = 'Weekly hours', timeZoneLabel, weekStartsOn = 1, locale, hourCycle, step = 15, defaultInterval = { start: 540, end: 1020 }, maxIntervals = 6, readOnly = false, disabled, name, labels, className, ...rest }, ref) {
    const uid = useId();
    const hc = hourCycle === '12' ? 'h12' : hourCycle === '24' ? 'h23' : undefined;
    const [inner, setInner] = useState(defaultValue ?? []);
    const value = valueProp ?? inner;
    const days = useMemo(() => byDay(value), [value]);
    const errors = useMemo(() => validateWeeklyHours(value), [value]);
    // Intervals of a day switched off, restored if it is switched back on.
    const remembered = useRef(new Map());
    const commit = useCallback((next) => {
        const flat = flatten(next);
        if (valueProp === undefined)
            setInner(flat);
        onValueChange?.(flat, validateWeeklyHours(flat));
    }, [valueProp, onValueChange]);
    const setDay = (day, ranges) => {
        const next = new Map(days);
        next.set(day, ranges);
        commit(next);
    };
    const L = {
        unavailable: labels?.unavailable ?? 'Unavailable',
        start: labels?.start ?? ((d, n) => `${d} interval ${n} start`),
        end: labels?.end ?? ((d, n) => `${d} interval ${n} end`),
        add: labels?.add ?? ((d) => `Add ${d} interval`),
        remove: labels?.remove ?? ((d, n) => `Remove ${d} interval ${n}`),
        copyToAll: labels?.copyToAll ?? ((d) => `Copy to all days from ${d}`),
        copyToAllShort: labels?.copyToAllShort ?? 'Copy to all',
        errors: { ...DEFAULT_ERRORS, ...labels?.errors },
    };
    const step_ = Math.max(1, Math.min(60, step));
    const options = useMemo(() => {
        const list = [];
        for (let m = 0; m <= 1440; m += step_)
            list.push(m);
        return list;
    }, [step_]);
    const optionsFor = (min, max, current) => {
        const list = options.filter((m) => m >= min && m <= max);
        if (!list.includes(current))
            list.push(current);
        return list
            .sort((a, b) => a - b)
            .map((m) => ({ value: String(m), label: timeLabel(m, locale, hc) }));
    };
    const isoStart = weekStartsOn === 0 ? 7 : weekStartsOn;
    const order = Array.from({ length: 7 }, (_, i) => ((isoStart - 1 + i) % 7) + 1);
    return (_jsxs("fieldset", { ref: ref, ...rest, disabled: disabled, className: cx('mn-weekly-hours-editor', className), "data-readonly": readOnly || undefined, children: [_jsx("legend", { className: "mn-weekly-hours-editor__legend", children: legend }), timeZoneLabel ? _jsx("div", { className: "mn-weekly-hours-editor__zone", children: timeZoneLabel }) : null, name ? _jsx("input", { type: "hidden", name: name, value: JSON.stringify(flatten(days)) }) : null, _jsx("ul", { className: "mn-weekly-hours-editor__days", children: order.map((day) => {
                    const ranges = days.get(day) ?? [];
                    const on = ranges.length > 0;
                    const label = dayName(day, locale);
                    const labelId = `${uid}-day-${day}`;
                    const switchId = `${uid}-switch-${day}`;
                    const errorId = `${uid}-error-${day}`;
                    const error = errors[day];
                    return (_jsxs("li", { className: "mn-weekly-hours-editor__day", "data-day": day, "data-on": on || undefined, "data-invalid": error ? '' : undefined, children: [_jsx("div", { className: "mn-weekly-hours-editor__day-head", children: readOnly ? (_jsx("span", { className: "mn-weekly-hours-editor__day-name", id: labelId, children: label })) : (_jsxs(_Fragment, { children: [_jsx(Switch, { id: switchId, size: "sm", checked: on, "aria-labelledby": labelId, onCheckedChange: (checked) => {
                                                if (checked) {
                                                    const back = remembered.current.get(day);
                                                    setDay(day, back?.length ? back : [{ ...defaultInterval }]);
                                                }
                                                else {
                                                    remembered.current.set(day, ranges);
                                                    setDay(day, []);
                                                }
                                            } }), _jsx("label", { className: "mn-weekly-hours-editor__day-name", id: labelId, htmlFor: switchId, children: label })] })) }), _jsxs("div", { className: "mn-weekly-hours-editor__intervals", role: "group", "aria-labelledby": labelId, children: [!on ? (_jsx("p", { className: "mn-weekly-hours-editor__off", children: L.unavailable })) : readOnly ? (_jsx("p", { className: "mn-weekly-hours-editor__text", children: ranges
                                            .map((r) => `${timeLabel(r.start, locale, hc)} – ${timeLabel(r.end, locale, hc)}`)
                                            .join(', ') })) : (ranges.map((r, i) => (_jsxs("div", { className: "mn-weekly-hours-editor__interval", children: [_jsx(NativeSelect, { size: "sm", "aria-label": L.start(label, i + 1), invalid: !!error, "aria-describedby": error ? errorId : undefined, value: String(r.start), options: optionsFor(0, 1440 - step_, r.start), onChange: (e) => setDay(day, ranges.map((x, j) => j === i ? { ...x, start: Number(e.target.value) } : x)) }), _jsx("span", { className: "mn-weekly-hours-editor__dash", "aria-hidden": true, children: "\u2013" }), _jsx(NativeSelect, { size: "sm", "aria-label": L.end(label, i + 1), invalid: !!error, "aria-describedby": error ? errorId : undefined, value: String(r.end), options: optionsFor(step_, 1440, r.end), onChange: (e) => setDay(day, ranges.map((x, j) => j === i ? { ...x, end: Number(e.target.value) } : x)) }), _jsx(IconButton, { size: "sm", label: L.remove(label, i + 1), icon: _jsx(CloseGlyph, {}), onClick: () => {
                                                    const next = ranges.filter((_, j) => j !== i);
                                                    if (next.length === 0)
                                                        remembered.current.set(day, ranges);
                                                    setDay(day, next);
                                                } }), i === ranges.length - 1 && ranges.length < maxIntervals ? (_jsx(IconButton, { size: "sm", label: L.add(label), icon: _jsx(PlusGlyph, {}), onClick: () => setDay(day, [...ranges, nextInterval(ranges, step_)]) })) : null] }, i)))), error ? (_jsx("p", { className: "mn-weekly-hours-editor__error", id: errorId, role: "alert", children: L.errors[error] })) : null] }), !readOnly && on ? (_jsx(Button, { size: "sm", variant: "ghost", className: "mn-weekly-hours-editor__copy", leadingIcon: _jsx(CopyGlyph, {}), "aria-label": L.copyToAll(label), onClick: () => {
                                    const next = new Map();
                                    for (let d = 1; d <= 7; d++)
                                        next.set(d, ranges.map((x) => ({ ...x })));
                                    commit(next);
                                }, children: L.copyToAllShort })) : null] }, day));
                }) })] }));
});
//# sourceMappingURL=weekly-hours-editor.js.map